import {
  AxiosReviver, AxiosSerializer, CaseMetaData, Contexts, CoreConfigurationInterface,
  DataSynchStorageInterface, getContainer, KeyStorageInterface, NamedBindings, PartialStoredCase, Stringifier
} from "@dandelion/core";
import axios, { AxiosRequestTransformer, AxiosResponseTransformer } from "axios";
import _ from 'lodash';
import { injectable } from "inversify";
import { Crypt } from "../utils/Crypt";
import { UserService } from "../services/UserService";

@injectable()
export class DataSynchStorage implements DataSynchStorageInterface {
  protected SYNCH_BASE_URL = import.meta.env.VITE_APP_API_SYNCH_URL;
  protected synchKey = '';
  protected userService: UserService;
  protected coreConfiguration: CoreConfigurationInterface;
  protected keyStorage: KeyStorageInterface;

  protected axiosInstance = axios.create({
    transformRequest: [AxiosSerializer, ...(axios.defaults.transformRequest as AxiosRequestTransformer[])],
    transformResponse: [...(axios.defaults.transformResponse as AxiosResponseTransformer[]), AxiosReviver],
  });


  constructor(
  ) {
    this.userService = getContainer(Contexts.SHARED).get(NamedBindings.UserService) as UserService;
    this.coreConfiguration = getContainer(Contexts.DEFAULT).get(NamedBindings.CoreConfiguration) as CoreConfigurationInterface;
    this.keyStorage = getContainer(Contexts.DEFAULT).get(NamedBindings.KeyStorage) as KeyStorageInterface;

    this.axiosInstance.interceptors.request.use((config: any) => {
      config.headers = this.userService.getHeaders();
      return config;
    });
  }

  async permanentlyDelete(_uuid: string): Promise<void> {
    await this.axiosInstance.post(
      this.SYNCH_BASE_URL +
      `/RemoveDocument?collectionName=NewDandelionProsafeTest&condition={"caseMetaData.caseID":"${_uuid}"}`
    );
  }

  async saveSynch(partialStoredCases: PartialStoredCase[], caseMetaData: CaseMetaData): Promise<void> {
    if (partialStoredCases.length <= 0) {
      return;
    }
    try {
      this.synchKey = await this.keyStorage.getKey('SynchKey', 'synch');
    }
    catch (e: any) {
      console.info("Failed synchronization for problems with getting key: " +  (e?.message ?? e));
      return;
    }

    const data = partialStoredCases.reduce((prev, curr) => {
      if (curr.accessLevel == 'sensitiveData') {
        return prev;
      }
      if (curr.accessLevel == 'notToSynchronize') {
        return prev;
      }
      prev[curr.accessLevel] = curr;
      return prev;
    }, {} as { [i: string]: any });

    const model: { [i: string]: any } = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const p = data[key];
        const canReadPartialCase = this.userService.hasReadAccessRights(p.accessLevel);

        if (canReadPartialCase && this.coreConfiguration.encrypt) {
          const key = await this.keyStorage.getKey(caseMetaData.caseID, p.accessLevel);
          _.merge(model, await Crypt.decrypt(p.data, key));
        } else if (canReadPartialCase) {
          const parsedData = new Stringifier().deserialize(p.data);
          _.merge(model, parsedData);
          for (const d of Object.getOwnPropertySymbols(parsedData)) {
            // necessary
            //@ts-expect-error
            model[d] = _.cloneDeep(parsedData[d]);
          }
        }
      }
    }
    const encryptedModel = await Crypt.encrypt(model, this.synchKey);
    await this.axiosInstance.post(
      this.SYNCH_BASE_URL +
      `/SetDocument?collectionName=NewDandelionProsafeTest&condition={"caseMetaData.caseID":"${caseMetaData.caseID}","caseMetaData.centreCode":"${caseMetaData.centreCode}"}`,
      { data: encryptedModel, caseMetaData }
    );
  }
}

